import { IconClose } from "@/assets/icons/geist/IconClose";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import {
    WorkflowEditFormTriggerContextType,
    type WorkflowEditFormTriggerContext,
} from "@/components/automations/automation-edit-form-data";
import { RepoFilteringContent } from "@/components/automations/AutomationEditRepoCodeContext";
import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { Text } from "@/components/podkit/typography/Text";
import { useEnvironmentClassEntries, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useListProjects, type PlainProject } from "@/queries/project-queries";
import { deduplicateUrls, normalizeRepositoryUrl } from "@/utils/csv-import";
import type React from "react";
import { useCallback, useMemo, type FC } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { useFlagAutomationsRepoListModeEnabled } from "@/hooks/use-flag";
import { RepositoryListContextContent } from "@/components/automations/AutomationEditRepoListCodeContext";
import { ProjectCombobox } from "@/components/projects/ProjectCombobox";
import { EnvironmentClassSelector } from "@/components/automations/EnvironmentClassSelector";

type WorkflowEditCodeContextProps = {
    context?: WorkflowEditFormTriggerContext;
    onChange: (context: WorkflowEditFormTriggerContext) => void;
    disabled?: boolean;
};

export const MAX_REPO_URLS = 100;

export const WorkflowEditCodeContext: FC<WorkflowEditCodeContextProps> = ({ context, onChange, disabled = false }) => {
    const { value: repoListModeEnabled } = useFlagAutomationsRepoListModeEnabled();
    const { environmentClassEntries } = useEnvironmentClassEntries({ enabled: true });

    const CONTEXT_ITEMS = useMemo(() => {
        return {
            [WorkflowEditFormTriggerContextType.Projects]: {
                name: "Projects",
                description: "Run steps across your Ona projects",
                enabled: true,
            },
            [WorkflowEditFormTriggerContextType.RepositoriesSearch]: {
                name: repoListModeEnabled ? "Repositories - Search" : "Repositories",
                description: "Target code repositories by filter or search",
                enabled: true,
            },
            [WorkflowEditFormTriggerContextType.RepositoriesList]: {
                name: "Repositories - List",
                description: "Target code repositories by explicit list",
                enabled: repoListModeEnabled,
            },
        } as const;
    }, [repoListModeEnabled]);

    const handleTargetChange = (value: WorkflowEditFormTriggerContextType) => {
        let nextContext: WorkflowEditFormTriggerContext;
        switch (value) {
            case WorkflowEditFormTriggerContextType.Projects:
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: [],
                };
                break;
            case WorkflowEditFormTriggerContextType.RepositoriesSearch: {
                const defaultEntry = environmentClassEntries[0];
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.RepositoriesSearch,
                    repoSearchString: "",
                    scmHost: "",
                    runnerId: "",
                    environmentClassId: defaultEntry?.clazz.id ?? "",
                };
                break;
            }
            case WorkflowEditFormTriggerContextType.RepositoriesList: {
                const defaultEntry = environmentClassEntries[0];
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.RepositoriesList,
                    repoUrls: [],
                    environmentClassId: defaultEntry?.clazz.id ?? "",
                };
                break;
            }
            default:
                nextContext = {
                    type: WorkflowEditFormTriggerContextType.Projects,
                    projectIds: [],
                };
                break;
        }
        onChange(nextContext);
    };

    const handleProjectSelectionChange = useCallback(
        (projectIds: string[]) => {
            if (context?.type === WorkflowEditFormTriggerContextType.Projects) {
                onChange({
                    ...context,
                    projectIds,
                });
            }
        },
        [context, onChange],
    );

    const handleEnvironmentClassSelectionChange = useCallback(
        (selectedEntry: EnvironmentClassEntry) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch) {
                if (
                    context.runnerId !== selectedEntry.runner.runnerId ||
                    context.environmentClassId !== selectedEntry.clazz.id
                ) {
                    onChange({
                        ...context,
                        runnerId: selectedEntry.runner.runnerId,
                        environmentClassId: selectedEntry.clazz.id,
                    });
                }
            } else if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesList) {
                if (context.environmentClassId !== selectedEntry.clazz.id) {
                    onChange({
                        ...context,
                        environmentClassId: selectedEntry.clazz.id,
                    });
                }
            }
        },
        [context, onChange],
    );

    const handleRemoveProject = useCallback(
        (projectId: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.Projects) {
                onChange({
                    ...context,
                    projectIds: context?.projectIds?.filter((id: string) => id !== projectId) || [],
                });
            }
        },
        [context, onChange],
    );

    const handleClearAllProjects = useCallback(() => {
        if (context?.type === WorkflowEditFormTriggerContextType.Projects) {
            onChange({
                ...context,
                projectIds: [],
            });
        }
    }, [context, onChange]);

    const handleAddProject = useCallback(
        (projectId: string) => {
            if (
                context?.type === WorkflowEditFormTriggerContextType.Projects &&
                !context.projectIds.includes(projectId)
            ) {
                handleProjectSelectionChange([...context.projectIds, projectId]);
            }
        },
        [context, handleProjectSelectionChange],
    );

    const handleAddRepoSearchString = useCallback(
        (filter: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch) {
                onChange({ ...context, repoSearchString: filter });
            }
        },
        [context, onChange],
    );

    const handleClearRepoSearchString = useCallback(() => {
        if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch) {
            onChange({ ...context, repoSearchString: "" });
        }
    }, [context, onChange]);

    const handleScmHostChange = useCallback(
        (scmHost: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesSearch) {
                onChange({
                    ...context,
                    scmHost,
                });
            }
        },
        [context, onChange],
    );

    const handleAddRepoUrls = useCallback(
        (urls: string[]) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesList) {
                const normalized = urls.map(normalizeRepositoryUrl).filter(Boolean);
                const allUrls = [...context.repoUrls, ...normalized];
                const deduplicated = deduplicateUrls(allUrls).slice(0, MAX_REPO_URLS);
                onChange({
                    ...context,
                    repoUrls: deduplicated,
                });
            }
        },
        [context, onChange],
    );

    const handleRemoveRepoUrl = useCallback(
        (url: string) => {
            if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesList) {
                onChange({
                    ...context,
                    repoUrls: context.repoUrls.filter((u) => u !== url),
                });
            }
        },
        [context, onChange],
    );

    const handleClearAllRepoUrls = useCallback(() => {
        if (context?.type === WorkflowEditFormTriggerContextType.RepositoriesList) {
            onChange({
                ...context,
                repoUrls: [],
            });
        }
    }, [context, onChange]);

    // MARK: render ctx selectors

    let contextSelectors: React.ReactNode;
    let projectSelector: React.ReactNode = null;
    switch (context?.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            projectSelector = (
                <ProjectCombobox
                    selectedProject={null}
                    onProjectChange={handleAddProject}
                    excludeProjectIds={context.projectIds}
                    placeholder="Add a project…"
                    className="w-fit"
                    disabled={disabled}
                />
            );
            break;
        case WorkflowEditFormTriggerContextType.RepositoriesSearch:
        case WorkflowEditFormTriggerContextType.RepositoriesList:
            contextSelectors = (
                <EnvironmentClassSelector
                    selectedEnvironmentClassId={context.environmentClassId}
                    disabled={disabled}
                    onSelectionChange={handleEnvironmentClassSelectionChange}
                />
            );
            break;
    }

    // displays the selected context, eg selected projects or repository search string
    let selectedContextContent: React.ReactNode;
    switch (context?.type) {
        case WorkflowEditFormTriggerContextType.Projects:
            selectedContextContent = (
                <SelectedProjectsContent
                    selectedProjectIds={context.projectIds}
                    disabled={disabled}
                    onRemoveProject={handleRemoveProject}
                    onClearAll={handleClearAllProjects}
                />
            );
            break;
        case WorkflowEditFormTriggerContextType.RepositoriesSearch: {
            if (context?.runnerId) {
                selectedContextContent = (
                    <RepoFilteringContent
                        disabled={disabled}
                        runnerId={context?.runnerId}
                        repoSearchString={context?.repoSearchString}
                        scmHost={context?.scmHost}
                        onScmHostChange={handleScmHostChange}
                        onAddFilter={handleAddRepoSearchString}
                        onClearFilter={handleClearRepoSearchString}
                    />
                );
            } else {
                selectedContextContent = null;
            }
            break;
        }
        case WorkflowEditFormTriggerContextType.RepositoriesList:
            selectedContextContent = (
                <RepositoryListContextContent
                    context={context}
                    runnerId={
                        environmentClassEntries.find((ev) => ev.clazz.id === context.environmentClassId)?.clazz
                            .runnerId ?? ""
                    }
                    disabled={disabled}
                    onAddUrls={handleAddRepoUrls}
                    onRemoveUrl={handleRemoveRepoUrl}
                    onClearAll={handleClearAllRepoUrls}
                />
            );
            break;
        default:
            selectedContextContent = null;
            break;
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-row items-end justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <Text className="text-base font-medium text-content-primary">Runs on</Text>
                    <RadioGroup
                        value={context?.type}
                        onValueChange={(value) => handleTargetChange(value as WorkflowEditFormTriggerContextType)}
                        disabled={disabled}
                        className="flex flex-row items-center gap-4"
                    >
                        {Object.entries(CONTEXT_ITEMS).map(([value, item]) => {
                            if (!item.enabled) {
                                return null;
                            }
                            return (
                                <label key={value} className="flex cursor-pointer items-center gap-2">
                                    <RadioGroupItem value={value} disabled={!item.enabled} />
                                    <Text className="text-base font-medium text-content-primary">{item.name}</Text>
                                </label>
                            );
                        })}
                    </RadioGroup>
                </div>
                {projectSelector}
            </div>
            {contextSelectors && <div className="flex flex-row items-center">{contextSelectors}</div>}
            {selectedContextContent}
        </div>
    );
};

// MARK: project code context

const SelectedProjectsContent: FC<{
    selectedProjectIds: string[];
    disabled: boolean;
    onRemoveProject: (projectId: string) => void;
    onClearAll: () => void;
}> = ({ selectedProjectIds, disabled, onRemoveProject, onClearAll }) => {
    const { data: projectsData, isLoading } = useListProjects({
        enabled: selectedProjectIds.length > 0,
        projectIds: selectedProjectIds,
    });

    const isEmpty = selectedProjectIds.length === 0 || (projectsData?.projects.length === 0 && !isLoading);

    return (
        <div
            className={`flex min-h-[46px] w-full flex-row items-start gap-2 rounded-lg border px-3 py-2 transition-colors ease-out ${isEmpty ? "border-border-subtle" : "border-border-base"}`}
        >
            {isEmpty ? (
                <Text className="flex h-[30px] items-center text-base text-content-muted">
                    Add projects to run the automation for each selected project.
                </Text>
            ) : (
                <>
                    <div className="flex flex-1 flex-row flex-wrap items-center gap-2">
                        {projectsData?.projects.map((project: PlainProject) => (
                            <Pill key={project.id} className="w-fit" variant="neutral" size="lg">
                                <div className="flex items-center gap-1">
                                    <span className="truncate text-sm font-bold">{project.metadata?.name}</span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="xs"
                                        disabled={disabled}
                                        onClick={() => onRemoveProject(project.id)}
                                        LeadingIcon={(_size) => <IconClose size="sm" className="size-3" />}
                                        className="size-4"
                                        data-tracking-id="remove-project-trigger-project-selector"
                                    />
                                </div>
                            </Pill>
                        ))}
                    </div>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onClearAll}
                        className="flex min-h-[28px] flex-shrink-0 items-center text-content-secondary hover:text-content-primary disabled:opacity-50"
                        data-tracking-id="clear-all-projects"
                    >
                        <IconCrossSmall />
                    </button>
                </>
            )}
        </div>
    );
};
